import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TimeoutError } from 'rxjs';
import { ApiProblem } from './api-problem.type';

const GENERIC_ERROR: ApiProblem = {
  title: 'Não foi possível concluir a solicitação.',
  status: 500,
};

@Injectable({
  providedIn: 'root',
})
export class ApiErrorService {
  normalize(error: unknown): ApiProblem {
    if (error instanceof TimeoutError) {
      return {
        title: 'Tempo limite excedido.',
        status: 408,
        detail: 'A API demorou mais que o esperado para responder.',
      };
    }

    if (!(error instanceof HttpErrorResponse)) {
      return GENERIC_ERROR;
    }

    if (error.status === 0) {
      return {
        title: 'API indisponível.',
        status: 0,
        detail: 'Não foi possível conectar ao servidor.',
      };
    }

    const problem = this.extractProblem(error);

    return {
      title: problem.title || this.titleForStatus(error.status),
      status: problem.status || error.status,
      detail: this.safeDetail(problem.detail, error.status),
      type: problem.type,
      instance: problem.instance,
      errors: problem.errors,
    };
  }

  toUserMessage(problem: ApiProblem): string {
    if (problem.status === 401) {
      return 'E-mail ou senha inválidos.';
    }

    if (problem.status === 0) {
      return 'Não foi possível conectar à API. Verifique se o backend está em execução.';
    }

    if (problem.status === 408) {
      return 'A API demorou para responder. Tente novamente em instantes.';
    }

    if (problem.status >= 500) {
      return 'Ocorreu um erro no servidor. Tente novamente mais tarde.';
    }

    return problem.detail || problem.title;
  }

  private extractProblem(error: HttpErrorResponse): Partial<ApiProblem> {
    if (this.isProblem(error.error)) {
      return error.error;
    }

    return {
      status: error.status,
      title: this.titleForStatus(error.status),
    };
  }

  private isProblem(value: unknown): value is ApiProblem {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return 'status' in value || 'title' in value || 'detail' in value;
  }

  private safeDetail(detail: string | undefined, status: number): string | undefined {
    if (!detail || detail.trim().startsWith('<')) {
      return undefined;
    }

    if (status === 401) {
      return undefined;
    }

    return detail;
  }

  private titleForStatus(status: number): string {
    const titles: Record<number, string> = {
      400: 'Requisição inválida.',
      401: 'Não autenticado.',
      403: 'Acesso negado.',
      404: 'Recurso não encontrado.',
      408: 'Tempo limite excedido.',
      409: 'Conflito de dados.',
      500: 'Erro interno do servidor.',
    };

    return titles[status] ?? 'Erro na solicitação.';
  }
}
